<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import MessageApi from 'dashboard/api/inbox/message';
import { useCapiStore } from 'dashboard/wasapi/capi/store';
import { outcomeLabelKey, outcomeMeta } from 'dashboard/wasapi/capi/outcomes';
import FepCountdown from 'dashboard/wasapi/capi/components/FepCountdown.vue';
import Button from 'next/button/Button.vue';
import Input from 'next/input/Input.vue';
import Dialog from 'next/dialog/Dialog.vue';

// Lead-outcome reporting for a Click-to-WhatsApp message. The ad itself is rendered by the
// upstream `WhatsappReferral` bubble; this only adds the Wasapi-specific Meta CAPI actions.
const props = defineProps({
  messageId: { type: [Number, String], required: true },
  conversationId: { type: [Number, String], required: true },
  inboxId: { type: [Number, String], required: true },
  contentAttributes: { type: Object, default: () => ({}) },
});

const { t } = useI18n();

const capiStore = useCapiStore();
onMounted(() => capiStore.fetch(props.inboxId));

const outcomeOptions = computed(() =>
  capiStore.enabledOutcomesFor(props.inboxId).map(key => ({
    key,
    requiresValue: capiStore.catalog.find(c => c.key === key)?.requires_value,
    ...outcomeMeta(key),
  }))
);
const outcomeLabel = key => t(outcomeLabelKey(key));

// Outcomes with their own confirmation copy; anything else falls back to the generic sentence.
const CONFIRM_KEYS = {
  qualified_lead: 'QUALIFIED_LEAD',
  sold: 'SOLD',
  order_canceled: 'ORDER_CANCELED',
  not_sold: 'NOT_SOLD',
};

const referral = computed(() => props.contentAttributes?.referral || {});
const outcomes = computed(() => {
  const list = props.contentAttributes?.ctwaOutcomes;
  if (Array.isArray(list)) return list;
  const single = props.contentAttributes?.ctwaOutcome;
  return single ? [single] : [];
});
const reportedStatuses = computed(() => outcomes.value.map(o => o.status));
const canReport = computed(() => Boolean(referral.value.ctwaClid));

const confirmDialogRef = ref(null);
const pendingOption = ref(null);
const saleValue = ref('');
const isSubmitting = ref(false);

const isSalePending = computed(() => !!pendingOption.value?.requiresValue);
const confirmTitle = computed(() =>
  pendingOption.value ? outcomeLabel(pendingOption.value.key) : ''
);
const confirmDescription = computed(() => {
  if (!pendingOption.value) return '';
  const known = CONFIRM_KEYS[pendingOption.value.key];
  return known
    ? t(`WASAPI_CAPI.CONFIRM.${known}`)
    : t('WASAPI_CAPI.CONFIRM_GENERIC', {
        outcome: outcomeLabel(pendingOption.value.key),
      });
});
const isConfirmDisabled = computed(
  () => isSubmitting.value || (isSalePending.value && !saleValue.value)
);

const statusText = outcome => {
  const label = outcomeLabel(outcome.status);
  const by = outcome.reportedByName || t('WASAPI_CAPI.REPORTED_BY_SYSTEM');
  const value = outcome.value
    ? ` (${outcome.currency} ${Number(outcome.value).toFixed(2)})`
    : '';
  return `${label}${value} · ${by}`;
};

const report = async payload => {
  isSubmitting.value = true;
  try {
    await MessageApi.reportCtwaConversion(
      props.conversationId,
      props.messageId,
      payload
    );
    useAlert(t('WASAPI_CAPI.REPORT_SUCCESS'));
    confirmDialogRef.value?.close();
    pendingOption.value = null;
    saleValue.value = '';
  } catch (error) {
    useAlert(error?.response?.data?.error || t('WASAPI_CAPI.REPORT_ERROR'));
  } finally {
    isSubmitting.value = false;
  }
};

const onOutcomeClick = option => {
  pendingOption.value = option;
  saleValue.value = '';
  confirmDialogRef.value?.open();
};

const onConfirm = () => {
  if (!pendingOption.value) return;
  const payload = { outcome: pendingOption.value.key };
  if (isSalePending.value) {
    payload.value = saleValue.value;
    payload.currency = 'BRL';
  }
  report(payload);
};
</script>

<template>
  <div class="flex flex-col gap-2 max-w-lg" data-testid="ctwa-outcome-actions">
    <span v-if="referral.sourceId" class="text-xs text-n-slate-11">
      {{ $t('WASAPI_CAPI.SOURCE_ID', { id: referral.sourceId }) }}
    </span>

    <FepCountdown v-if="conversationId" :conversation-id="conversationId" />

    <template v-if="canReport">
      <ul v-if="outcomes.length" class="flex flex-col gap-1 p-0 list-none">
        <li
          v-for="(outcome, index) in outcomes"
          :key="index"
          class="flex items-center gap-2 px-2 py-1 text-xs rounded-lg bg-n-alpha-2 text-n-slate-11"
        >
          <span class="i-lucide-check size-3.5 text-n-teal-11" />
          {{ statusText(outcome) }}
        </li>
      </ul>
      <div class="grid grid-cols-2 gap-2">
        <Button
          v-for="option in outcomeOptions"
          :key="option.key"
          size="sm"
          variant="outline"
          :color="option.color"
          :icon="option.icon"
          :label="outcomeLabel(option.key)"
          :disabled="isSubmitting || reportedStatuses.includes(option.key)"
          class="w-full"
          @click="onOutcomeClick(option)"
        />
      </div>
      <Dialog
        ref="confirmDialogRef"
        type="edit"
        width="sm"
        :title="confirmTitle"
        :description="confirmDescription"
        :is-loading="isSubmitting"
        :disable-confirm-button="isConfirmDisabled"
        @confirm="onConfirm"
        @close="pendingOption = null"
      >
        <Input
          v-if="isSalePending"
          v-model="saleValue"
          type="number"
          :label="t('WASAPI_CAPI.VALUE_LABEL')"
          :placeholder="t('WASAPI_CAPI.VALUE_PLACEHOLDER')"
        />
      </Dialog>
    </template>
  </div>
</template>
